import { useTranslation } from 'react-i18next';
import { gogLink } from '../lib/affiliate';

const JACKET = (appId) =>
  `https://cdn.cloudflare.steamstatic.com/steam/apps/${appId}/header.jpg`;

// Prix GOG formaté (EUR → "9,99 €", sinon "9.99 USD").
function formatPrice(amount, currency) {
  if (amount == null) return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  if ((currency || 'EUR').toUpperCase() === 'EUR') {
    return `${n.toFixed(2).replace('.', ',')} €`;
  }
  return `${n.toFixed(2)} ${currency}`;
}

// Priorité de sauvegarde = gravité du risque × ampleur du temps de jeu.
// Plus tu y as joué et plus c'est menacé, plus il faut le racheter DRM-free en premier.
function priorityScore(item) {
  const sev = item.severity || 0;
  const hours = Math.round((item.playtimeMinutes || 0) / 60);
  return sev * (hours + 1);
}

function SaveList({ items, isPro }) {
  const { t } = useTranslation();

  // Uniquement les jeux à risque ET rachetables DRM-free, triés par priorité.
  const ranked = (items || [])
    .filter((i) => i.savable && i.gogUrl)
    .sort((a, b) => priorityScore(b) - priorityScore(a));

  // Statut Pro en cours de chargement : on ne rend rien pour éviter le flash.
  if (isPro === null) return null;

  // Non-Pro : upsell.
  if (!isPro) {
    return (
      <section className="savelist savelist--locked">
        <span className="savelist__lock" aria-hidden="true">🔒</span>
        <h2 className="savelist__title">{t('savelist.title')}</h2>
        <p className="savelist__subtitle">{t('savelist.locked')}</p>

        <div className="savelist__explain">
          <span className="savelist__explain-title">{t('savelist.explain_title')}</span>
          <p className="savelist__explain-body">{t('savelist.explain_body')}</p>
          <span className="savelist__explain-formula">{t('savelist.explain_formula')}</span>
        </div>

        <a className="btn btn--amber" href="/tarifs">{t('savelist.go_pro')}</a>
      </section>
    );
  }

  // Pro mais rien à sauver.
  if (ranked.length === 0) {
    return (
      <section className="savelist">
        <span className="savelist__pro-badge">PRO ✓</span>
        <h2 className="savelist__title">{t('savelist.title')}</h2>
        <p className="savelist__empty">{t('savelist.empty')}</p>
      </section>
    );
  }

  return (
    <section className="savelist">
      <span className="savelist__pro-badge">PRO ✓</span>
      <h2 className="savelist__title">{t('savelist.title')}</h2>
      <p className="savelist__subtitle">{t('savelist.subtitle')}</p>

      <ol className="savelist__list">
        {ranked.map((i, idx) => {
          const hours = Math.round((i.playtimeMinutes || 0) / 60);
          const price = formatPrice(i.gogPrice, i.gogCurrency);
          return (
            <li key={i.appId} className="savelist__row">
              <span className="savelist__rank">#{idx + 1}</span>
              <img
                className="savelist__jacket"
                src={JACKET(i.appId)}
                alt=""
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.visibility = 'hidden';
                }}
              />
              <div className="savelist__info">
                <span className="savelist__name">{i.name}</span>
                <span className="savelist__meta">
                  <span className={`savelist__risk savelist__risk--${i.riskType}`}>
                    {t(`risk.${i.riskType}`)}
                  </span>
                  {hours > 0 && (
                    <span className="savelist__hours">
                      {t('savelist.playtime', { hours })}
                    </span>
                  )}
                  {price && (
                    <span className="savelist__price">
                      {price}
                      {i.gogDiscount > 0 && (
                        <span className="savelist__discount">-{i.gogDiscount}%</span>
                      )}
                    </span>
                  )}
                </span>
              </div>
              <a
                className="btn btn--mint savelist__cta"
                href={gogLink(i.gogUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {price ? t('savelist.buy_price', { price }) : t('savelist.buy')}
              </a>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default SaveList;
